import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import ProgressWizard from 'components/field/ProgressWizard';
import Form from 'components/form/Form';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';

import CreateShipmentWithMyRatesForm from 'pages/Shipments/ActionForms/CreateShipment/CreateShipmentWithMyRatesForm';
import OrderAvailableForm from 'pages/Shipments/ActionForms/OrderAvailableForm';

import { ADVANCE_WMS_MODULE, ORDERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faBan } from '@fortawesome/pro-solid-svg-icons';

import OrderDetails from './OrderDetails';
import Packing from './Packing';

const PackingForm = NiceModal.create(({ id, modalName, initialTab = 0, initialPicks = [], mode, reloadItems }) => {
	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const modal = useModal();
	const { closeModal } = useModalRouter();
	const { showError, showSuccess } = useAlert();
	const dispatch = useDispatch();

	const withMyRatesFormRef = useRef();
	const [completePackingRef, setCompletePackingRef] = useState(null);
	const [tabStep, setTabStep] = useState(initialTab);
	const [loading, setLoading] = useState(true);
	const [isCreateShipmentInitialised, setIsCreateShipmentInitialised] = useState(false);
	const [successCount, setSuccessCount] = useState(0);
	const [failedCount, setFailedCount] = useState(0);
	const [isLoadingNotAvailable, setIsLoadingNotAvailable] = useState(false);
	const { Currency } = useLocale();
	const [stages, setStages] = useState([{ name: 'orderDetails' }, { name: 'shipmentDetails' }, { name: 'pack' }]);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			modal.resolve();
			closeModal();
		} else {
			modal.resolve();
			modal.remove();
		}
	}, []);

	const schema = yup.object().shape({
		orderID: yup.number().nullable(),
		orderDate: yup.string().nullable(),
		shippingDescription: yup.string().nullable(),
		shipmentNumber: yup.string().nullable(),
		packingStatus: yup
			.string()
			.nullable()
			.test('error.alreadyPacked', t('error.alreadyPacked'), (packingStatus) => packingStatus !== 'packed'),
		products: yup
			.array()
			.of(
				yup.object().shape({
					productID: yup.number().nullable(),
					pickedQty: yup.number().nullable()
				})
			)
			.nullable()
	});

	const fillForm = useCallback(async () => {
		const defaultValues = await axios
			.post(ORDERS.GET_ORDER_BY_ID, { ID: Number(id) })
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					const calculateChecked = (a) => {
						const pickedQTY = initialPicks.find((b) => b.SKU === a.SKU)?.qty ?? 0;
						return pickedQTY === a.quantity;
					};

					if (data.shipmentDetails.shipmentID) {
						setStages([{ name: 'orderDetails' }, { name: 'pack' }]);
					}

					return {
						orderID: id ?? null,
						orderIDShowed: data.orderDetails.orderID ?? null,
						orderDate: data.orderDetails.orderDate ?? null,
						shipmentNumber: data.shipmentDetails.shipmentID ?? null,
						shippingDescription: data.shipmentDetails.shippingDescription ?? null,
						logoUrl: data.shipmentDetails.deliveryCompanyLogo ?? null,
						originAddress: {
							city: data.shipmentDetails.originCity ?? null
						},
						destinationAddress: {
							city: data.shipmentDetails.destinationCity ?? null
						},
						trackingURL: data.shipmentDetails.trackingURL ?? null,
						totalWeight: data.shipmentDetails.boxWeight ?? null,
						packingStatus: data?.orderDetails?.packingStatus ?? null,
						products: data.itemsDetails
							.filter((item) => item.itemType !== 'bundle')
							.map((item) => ({
								...item,
								pickedQTY: initialPicks.find((a) => a.SKU === item.SKU)?.qty ?? 0,
								checked: calculateChecked(item)
							})),
						status: data.shipmentDetails.status || null,
						otoPackPackingPageAutomaticPrintAWB: data.customerDetails.otoPackPackingPageAutomaticPrintAWB,
						otoPackPackingPageAutomaticShipment: data.customerDetails.otoPackPackingPageAutomaticShipment,
						createShipmentData: {
							items: [
								{
									ID: id,
									otoID: id,
									orderID: data.orderDetails.orderID ?? null,
									manualShipment: false,
									dcSettingsID: null,
									packageWeight:
										data.orderDetails.weight !== undefined ? data.orderDetails.weight : 1,
									customCurrency: Currency,
									boxes: data.boxes ?? null,
									packageCount: data.orderDetails.packageCount ?? 1
								}
							]
						}
					};
				} else {
					AppLogger.error(response);
					showError({
						message: response?.data?.code
					});
					handleCloseModal();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: error.response?.data?.code
				});
				handleCloseModal();
			});

		return defaultValues;
	}, []);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		control,
		setValue,
		handleSubmit,
		watch,
		formState: { isLoading: isFormLoading, isSubmitting }
	} = formMethods;

	const isLoading = isSubmitting || isFormLoading;

	const shipmentNumber = useWatch({ control, name: 'shipmentNumber' });
	const products = useWatch({ control, name: 'products' });
	const status = useWatch({ control, name: 'status' });
	const otoPackPackingPageAutomaticShipment = useWatch({ control, name: 'otoPackPackingPageAutomaticShipment' });

	const nextStep = (i) => {
		setTabStep(i ?? tabStep + 1);
	};

	useEffect(() => {
		if (tabStep !== 1 || shipmentNumber) return;

		if (successCount === 1 || failedCount === 1) {
			axios
				.post(ORDERS.GET_ORDER_BY_ID, { ID: Number(id) })
				.then((response) => {
					const { data, status } = response;

					if (status === 200) {
						setValue('shipmentNumber', data.shipmentDetails.shipmentID || null);
						setValue('shippingDescription', data.shipmentDetails.shippingDescription || null);
						setValue('logoUrl', data.shipmentDetails.deliveryCompanyLogo || null);
						setValue('originAddress', {
							city: data.shipmentDetails.originCity ?? null
						});
						setValue('destinationAddress', {
							city: data.shipmentDetails.destinationCity ?? null
						});
						setValue('trackingURL', data.shipmentDetails.trackingURL || null);
						setValue('totalWeight', data.shipmentDetails.boxWeight || null);

						setValue('packingStatus', data?.orderDetails?.packingStatus || null);

						setValue('status', data?.shipmentDetails?.status || null);
					} else {
						AppLogger.error(response);
						showError({
							message: response?.data?.code
						});
					}
				})
				.catch((error) => {
					AppLogger.error(error);
					showError({
						message: error.response?.data?.code
					});
				})
				.finally(() => {
					nextStep();
				});
		}
	}, [tabStep, shipmentNumber, successCount, failedCount]);

	useEffect(() => {
		if (tabStep === 1 && otoPackPackingPageAutomaticShipment && isCreateShipmentInitialised) {
			withMyRatesFormRef?.current?.handleCreateShipment();
		}
	}, [tabStep, isCreateShipmentInitialised]);

	const notAvailableAction = async () => {
		setIsLoadingNotAvailable(true);
		if (mode === 'quickPack') {
			NiceModal.show(OrderAvailableForm, {
				orderID: id,
				status,
				handleCloseModal
			});
		} else {
			try {
				await axios.post(ADVANCE_WMS_MODULE.NOT_AVAILABLE_ORDER, {
					otoIDs: [id]
				});
				showSuccess();
				handleCloseModal();
			} catch (error) {
				AppLogger.error(error);
				showError();
			}
		}
		setIsLoadingNotAvailable(false);
	};

	const handleCompletePacking = async () => {
		try {
			await axios.post(ADVANCE_WMS_MODULE.COMPLETE_PACKING, {
				otoID: id
			});
			dispatch(updateDynamicListData(DYNAMICLISTS.PACKING_LIST));
			showSuccess();
			reloadItems && reloadItems();
			handleCloseModal();
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const onCompletePackingError = (errors) => {
		if (
			errors?.packingStatus?.message === 'error.alreadyPacked' ||
			errors?.packingStatus?.type === 'error.alreadyPacked'
		) {
			showError({
				message: 'error.alreadyPacked'
			});
		}
		AppLogger.log(errors);
	};

	const isSerialNumberValid = useMemo(() => {
		const products = watch('products');
		if (!products) return true;
		return products.every((product) => {
			const serialNumber = product?.serialNumber?.split?.(',');
			return !(product?.serialRequirement === 'Required' && !serialNumber?.length);
		});
	}, [watch()]);

	return (
		<Modal name={modalName} maxWidth="x-large" fullwidth>
			<ModalTitle>
				<Typography variant="p1">pack</Typography>
			</ModalTitle>
			<ModalContent>
				<ProgressWizard stages={stages} setIndex={setTabStep} index={tabStep} gutterBottom />
				{tabStep === 0 && (
					<Form formMethods={formMethods} borderless>
						<OrderDetails id={id} nextStep={nextStep} isSerialNumberValid={isSerialNumberValid} />
					</Form>
				)}
				{tabStep === 1 && shipmentNumber && (
					<Form formMethods={formMethods} borderless>
						<Packing completePackingRef={completePackingRef} />
					</Form>
				)}
				{tabStep === 1 && !shipmentNumber && (
					<CreateShipmentWithMyRatesForm
						ref={withMyRatesFormRef}
						ids={[id]}
						setLoading={setLoading}
						setSuccessCount={setSuccessCount}
						setFailedCount={setFailedCount}
						setInitialised={setIsCreateShipmentInitialised}
					/>
				)}
				{tabStep === 2 && (
					<Form formMethods={formMethods} borderless>
						<Packing completePackingRef={completePackingRef} />
					</Form>
				)}
			</ModalContent>
			<ModalActions>
				{tabStep === 0 && (
					<Button
						label="notAvailable"
						color="var(--red)"
						icon={faBan}
						onClick={notAvailableAction}
						loading={isLoading || isLoadingNotAvailable}
					/>
				)}
				<Button variant="outline" onClick={handleCloseModal} label="cancel" />
				{tabStep === 0 && (
					<Button
						label="continue"
						onClick={() => {
							if (products.some(({ checked }) => !checked)) {
								showError({ message: 'packing.pleaseCheckAllProducts' });
								return;
							}
							if (!isSerialNumberValid) {
								showError({ message: 'errorMsg.serialNumberRequired' });
								return;
							}

							nextStep();
						}}
					/>
				)}
				{tabStep === 1 && !shipmentNumber && (
					<Button
						label="createShipment"
						onClick={async () => {
							await withMyRatesFormRef.current?.handleCreateShipment();
						}}
						loading={loading}
					/>
				)}
				{(tabStep === 1 || tabStep === 2) && shipmentNumber && (
					<Button
						label="completePacking"
						ref={setCompletePackingRef}
						onClick={handleSubmit(handleCompletePacking, onCompletePackingError)}
						loading={isLoading}
						disabled
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default PackingForm;
